<template>
  <div>
    <v-list v-if="!editMode">
      <div v-if="isEmpty(vehicle.meta)" class="text-subtitle-1 mt-3 mb-2">
        {{ $t('no_vehicle_metadata') }}
      </div>
      <div v-else>
        <template v-for="(value, key) of metadata">
          <v-list-item :key="key">
            <v-list-item-content>
              <div class="d-flex align-center">
                <div style="width: 300px">{{ $t(key) }}</div>
                <div>
                  <multi-time-display v-if="isDateField(key)" :time="value"/>
                  <span v-else>{{ value }}</span>
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>
          <v-divider :key="key + 'divider'"/>
        </template>
      </div>
    </v-list>
    <!-- note, I think this was intended to eventually be released to MB admins but it's still root only -->
    <div v-if="user.can('view_rootentity')">
      <div v-if="!editMode" class="d-flex justify-content-end">
        <v-btn color="primary lighten-1" class="ml-3 my-2" @click="triggerEdit">
          <v-icon class="mr-2 ml-n1">mdi-pencil</v-icon>
          {{ $t('edit' )}}
        </v-btn>
      </div>
      <div v-else>
        <template v-for="(entry, index) of editModel">
          <v-list-item :key="index">
            <v-list-item-content>
              <div class="d-flex align-center">
                <div style="width: 300px">
                  <v-autocomplete
                    auto-select-first
                    v-model="entry[0]"
                    dense
                    outlined
                    hide-details
                    class="pr-2"
                    :items="whiteListedFields"
                    :error-messages="errors[index]"/>
                </div>
                <div class="flex-grow-1">
                  <v-text-field
                    hide-details
                    outlined
                    dense
                    v-model="entry[1]"
                    :error-messages="errors[index]"/>
                </div>
                <div class="flex-grow-0 pl-4" style="width: 50px">
                  <v-btn
                    fab
                    dark
                    x-small
                    color="danger"
                    elevation="0"
                    @click="removeEntry(index)">
                    <v-icon>mdi-minus</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class="danger--text caption" :key="index + '_error'">
                {{ errors[index] }}
              </div>
            </v-list-item-content>
          </v-list-item>
          <v-divider :key="index + '_divider'"/>
        </template>
        <v-list-item>
          <v-btn color="success" @click="addEntry" class="my-2">
            <v-icon class="mr-1 ml-n1">mdi-plus</v-icon>
            {{ $t('add_entry') }}
          </v-btn>
        </v-list-item>
        <v-divider/>
        <v-list-item class="d-flex justify-content-end">
          <v-btn @click="cancelEdit" dark color="blue-grey">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn class="ml-2" :loading="saving" color="primary" @click="save">
            <v-icon class="mr-1 ml-n1">mdi-content-save</v-icon>
            {{ $t('save') }}
          </v-btn>
        </v-list-item>
        <v-divider/>
      </div>
    </div>
  </div>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import OldValidationMixin from '@/components/mixins/OldValidationMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { indexOf, get, forEach, map, reduce, isEmpty } from 'lodash'

export default {
  name: 'VehicleMetadata',
  components: {
    MultiTimeDisplay
  },
  mixins: [
    CurrentUserMixin,
    OldValidationMixin,
    ApiUtilitiesMixin
  ],
  props: {
    vehicle: Object
  },
  validations: {
    editModel: {
      $each: {
        required: (value) => {
          return value != null &&
            value.length === 2 &&
            !isEmpty(value[0]) &&
            !isEmpty(value[1])
        }
      }
    }
  },
  data () {
    return {
      // Whether the edit mode is currently enabled
      editMode: false,
      // A temporary data model for when the metadata is being edited.
      editModel: null,
      // Whether the edit form is currently being saved
      saving: false,
      // A list of fields that we will show and also allow admins to edit.
      whiteListedFields: [
        'type',
        'make',
        'model',
        'trailer_manufacturer',
        'unit_manufacturer',
        'registration_number',
        'chassis_number',
        'length',
        'gross_vehicle_weight',
        'in_service_date',
        'replacement_date',
        'cooler_unit_placement',
        'bulkhead_or_center_divider',
        'year'
      ],
      // A list of fields to treat as dates
      dateFields: [
        'in_service_date',
        'replacement_date'
      ]
    }
  },
  computed: {
    /**
     * A formatted list of the vehicle's metadata, filtered via whiteListedFields.
     * @returns {{}}
     */
    metadata () {
      const result = {}
      forEach(this.whiteListedFields, field => {
        const value = get(this.vehicle, 'meta.' + field)
        if (value != null) {
          result[field] = value
        }
      })
      return result
    },
    errors () {
      return map(this.$v.editModel.$each.$iter, validation => {
        if (get(validation, '$error') === true) {
          return this.$t('required')
        }
        return null
      })
    }
  },
  methods: {
    isEmpty: isEmpty,
    /**
     * Return true if the specified field is a date.
     * @param field
     * @returns {boolean}
     */
    isDateField (field) {
      return indexOf(this.dateFields, field) !== -1
    },
    /**
     * Trigger edit mode
     * @param event
     */
    triggerEdit (event) {
      this.editModel = map(this.vehicle.meta, (value, key) => [key, value])
      this.editMode = true
      event.preventDefault()
    },
    /**
     * Disable edit mode
     */
    cancelEdit () {
      this.editMode = false
    },
    /**
     * Add a new line to the edit model
     */
    addEntry () {
      this.editModel.push(['', ''])
    },
    /**
     * Remove a line from the edit model
     * @param index
     */
    removeEntry (index) {
      this.editModel.splice(index, 1)
    },
    /**
     * Save the updated vehicle metadata.
     * @returns {Promise<boolean>}
     */
    async save () {
      this.$v.$touch()
      if (this.$v.$invalid) return false
      this.vehicle.meta = reduce(this.editModel, (result, value) => {
        result[value[0]] = value[1]
        return result
      }, {})
      this.saving = true
      try {
        await this.$orm('Vehicle').update(this.vehicle.id, this.vehicle)
        this.editMode = false
        await this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$tc('metadata')} ${this.$t('generic_save_success')}`
        )
      } catch (error) {
        this.renderRecordSaveErrorFlashBanner(error)
        throw error
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
</style>
